import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import { identifyUser, captureException } from '@/lib/sentry';

interface AuthState {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  loading: boolean;
  isSlowLoad: boolean;
}

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const checkAdminRole = async (userId: string): Promise<boolean> => {
  const { data } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', userId)
    .eq('role', 'admin')
    .maybeSingle();
  return !!data;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    isAdmin: false,
    loading: true,
    isSlowLoad: false,
  });
  const initializedRef = useRef(false);

  const processSession = useCallback(async (session: Session | null) => {
    const user = session?.user ?? null;
    let isAdmin = false;
    if (user) {
      try {
        isAdmin = await checkAdminRole(user.id);
      } catch (err) {
        // Don't block auth on a role-lookup failure; default to non-admin
        // and report to Sentry so we know about RLS / network / DB issues.
        captureException(err, { source: 'auth_check_admin_role', userId: user.id });
      }
    }
    // Tag subsequent Sentry events with who's signed in. Clears on signout.
    identifyUser(user ? { id: user.id, email: user.email } : null);
    setState({ user, session, isAdmin, loading: false, isSlowLoad: false });
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Slow load indicator at 10s
    const slowTimeout = setTimeout(() => {
      if (isMounted) {
        setState(prev => prev.loading ? { ...prev, isSlowLoad: true } : prev);
      }
    }, 10_000);

    // Hard timeout at 20s
    const safetyTimeout = setTimeout(() => {
      if (isMounted) {
        setState(prev => prev.loading ? { ...prev, loading: false, isSlowLoad: false } : prev);
      }
    }, 20_000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, newSession) => {
        if (!isMounted) return;
        if (!initializedRef.current) return;
        await processSession(newSession);
      }
    );

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!isMounted) return;
      await processSession(session);
      initializedRef.current = true;
    });

    return () => {
      isMounted = false;
      clearTimeout(slowTimeout);
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, [processSession]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
